<template>
    <ModalShell
        :title="isEditMode ? 'Edit Physical Address' : 'Add Physical Address'"
        :subtitle="isEditMode ? 'Update the physical address in your Identity Vault' : `Enter the physical address you'd like to add to your Identity Vault`"
        width="w-11/12 lg:w-[500px]"
        divider-class="mt-4 mb-3"
    >
        <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19" fill="none">
                <path
                    d="M18.25 0.25H1.75C1.35218 0.25 0.970644 0.408035 0.68934 0.68934C0.408035 0.970644 0.25 1.35218 0.25 1.75V14.5C0.25 14.8978 0.408035 15.2794 0.68934 15.5607C0.970644 15.842 1.35218 16 1.75 16H3.25V17.5C3.25 17.6989 3.32902 17.8897 3.46967 18.0303C3.61032 18.171 3.80109 18.25 4 18.25C4.19891 18.25 4.38968 18.171 4.53033 18.0303C4.67098 17.8897 4.75 17.6989 4.75 17.5V16H15.25V17.5C15.25 17.6989 15.329 17.8897 15.4697 18.0303C15.6103 18.171 15.8011 18.25 16 18.25C16.1989 18.25 16.3897 18.171 16.5303 18.0303C16.671 17.8897 16.75 17.6989 16.75 17.5V16H18.25C18.6478 16 19.0294 15.842 19.3107 15.5607C19.592 15.2794 19.75 14.8978 19.75 14.5V1.75C19.75 1.35218 19.592 0.970644 19.3107 0.68934C19.0294 0.408035 18.6478 0.25 18.25 0.25ZM18.25 14.5H1.75V1.75H18.25V7.75H16.6863C16.4982 6.63745 15.899 5.63615 15.0075 4.94452C14.116 4.25289 12.9972 3.9214 11.8729 4.01576C10.7485 4.11012 9.70059 4.62343 8.93683 5.45397C8.17307 6.28451 7.74919 7.37167 7.74919 8.5C7.74919 9.62833 8.17307 10.7155 8.93683 11.546C9.70059 12.3766 10.7485 12.8899 11.8729 12.9842C12.9972 13.0786 14.116 12.7471 15.0075 12.0555C15.899 11.3639 16.4982 10.3625 16.6863 9.25H18.25V14.5ZM13.5475 7.75C13.3824 7.46404 13.1276 7.24054 12.8225 7.11418C12.5174 6.98782 12.1792 6.96565 11.8602 7.05111C11.5413 7.13657 11.2594 7.32489 11.0584 7.58686C10.8574 7.84882 10.7485 8.1698 10.7485 8.5C10.7485 8.8302 10.8574 9.15118 11.0584 9.41314C11.2594 9.67511 11.5413 9.86343 11.8602 9.94889C12.1792 10.0344 12.5174 10.0122 12.8225 9.88582C13.1276 9.75946 13.3824 9.53596 13.5475 9.25H15.1562C14.9737 9.95699 14.5396 10.5731 13.9352 10.983C13.3309 11.3928 12.5979 11.5681 11.8735 11.4761C11.1491 11.3841 10.4832 11.0311 10.0005 10.4832C9.51782 9.93529 9.25151 9.23018 9.25151 8.5C9.25151 7.76982 9.51782 7.0647 10.0005 6.51683C10.4832 5.96895 11.1491 5.61592 11.8735 5.52391C12.5979 5.43191 13.3309 5.60724 13.9352 6.01705C14.5396 6.42686 14.9737 7.04301 15.1562 7.75H13.5475Z"
                    fill="#181D27"
                />
            </svg>
        </template>
        <div class="flex flex-col items-start gap-2.5 self-stretch">
            <div ref="countryInputRef" class="input-div">
                <span>Country</span>
                <PrimeSelect
                    filter
                    v-model="country"
                    :options="countryOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Country"
                    @change="onCountryChange($event.value)"
                    @show="updateCountryPanelWidth"
                    :panelStyle="{ width: countryPanelWidth ? `${countryPanelWidth}px` : 'auto' }"
                    :pt="{ header: { style: { paddingTop: '4px' } } }"
                    class="w-full !rounded"
                >
                    <template #value="slotProps">
                        <div v-if="slotProps.value" class="flex items-center">
                            <img
                                :alt="countryOptions.find((opt) => opt.value === slotProps.value)?.label"
                                :src="`/flags/${slotProps.value.toLowerCase()}.svg`"
                                style="width: 18px"
                                class="mr-2"
                                loading="lazy"
                            />
                            <div class="text-base">{{ countryOptions.find((opt) => opt.value === slotProps.value)?.label }}</div>
                        </div>
                        <span v-else>
                            {{ slotProps.placeholder }}
                        </span>
                    </template>
                    <template #option="slotProps">
                        <div class="flex items-center">
                            <img
                                v-if="slotProps.option.value"
                                :alt="slotProps.option.label"
                                :src="`/flags/${slotProps.option.value.toLowerCase()}.svg`"
                                style="width: 18px"
                                class="mr-2"
                            />
                            <div>{{ slotProps.option.label }}</div>
                        </div>
                    </template>
                </PrimeSelect>
            </div>
            <div class="input-div">
                <span>Address Line 1</span>
                <prime-input-text 
                    v-model="line1" 
                    class="w-full" 
                    type="text" 
                    placeholder="Address Line 1" 
                    @keydown.enter="onEnter"
                    @keydown="handleAddressLineKeydown"
                    @input="handleAddressLineInput($event, 'line1')"
                />
            </div>
            <div class="input-div">
                <div class="flex justify-between items-center self-stretch">
                    <span>Address Line 2</span>
                    <span>Optional</span>
                </div>
                <prime-input-text 
                    v-model="line2" 
                    class="w-full" 
                    type="text" 
                    placeholder="Address Line 2" 
                    @keydown.enter="onEnter"
                    @keydown="handleAddressLineKeydown"
                    @input="handleAddressLineInput($event, 'line2')"
                />
            </div>
            <div class="city-state-div">
                <div class="input-div">
                    <span>City</span>
                    <prime-input-text 
                        v-model="city" 
                        class="w-full" 
                        type="text" 
                        placeholder="City" 
                        @keydown.enter="onEnter" 
                        @keydown="handleCityStateKeydown"
                        @input="handleCityStateInput($event, 'city')"
                    />
                </div>
                <div class="input-div">
                    <span>State/Region</span>
                    <PrimeSelect
                        v-if="stateOptions.length > 0"
                        v-model="state"
                        :options="stateOptions"
                        optionLabel="label"
                        optionValue="label"
                        placeholder="State/Region"
                        class="w-full !rounded"
                    />
                    <prime-input-text
                        v-else
                        v-model="state"
                        class="w-full"
                        type="text"
                        placeholder="State/Region"
                        @keydown.enter="onEnter"
                        @keydown="handleCityStateKeydown"
                        @input="handleCityStateInput($event, 'state')"
                    />
                </div>
            </div>
            <div class="input-div">
                <div class="flex justify-between items-center self-stretch">
                    <span>Postal Code</span>
                    <span>Optional</span>
                </div>

                <prime-input-text 
                    v-model="postal_code"
                    class="w-full"
                    type="text"
                    placeholder="Postal Code" 
                    @input="onPostalInput"
                    @keydown.enter="onEnter" 
                    @keydown="handlePostalKeydown" 
                    @wheel="disableScroll"
                    :maxlength="country === 'US' ? 5 : 20" 
                />
            </div>
        </div>
        <template #footer>
            <prime-button @click="closeDialog" class="p-btn-root_secondary" label="Cancel" />

            <prime-button
                :label="isEditMode ? 'Save Changes' : 'Add Physical Address'"
                variant="primary"
                :disabled="!country || !state || state.trim() === '' || city === ''"
                @click="submitAddress"
                :loading="isAddingAddress"
            />
        </template>
    </ModalShell>
</template>
<script setup lang="ts">
import { nextTick } from 'vue';
import countriesData from '@/assets/json/countries.json';
import ModalShell from '~/components/modals/shared/ModalShell.vue';
import { useVaultStore } from '~/stores/person/vault/vault';
import { ModalNames } from '~/types/modals';
import type { Address } from '~/types/person/person';

const { letterOnlyInputHandler } = useUtils();
const { showSuccess, showError, showWarning } = useGlobalToast();

const country = ref('');
const state = ref('');
const line1 = ref('');
const line2 = ref('');
const city = ref('');
const postal_code = ref('');
const stateOptions = ref<{ label: string; value: string }[]>([]);

// Track if data is prefilled from exposure
const isPrefilledFromExposure = ref(false);

const countryOptions = countriesData.map((country) => ({
    label: country.name,
    value: country.code,
}));

// Template ref for country input container to get its width for the dropdown panel
const countryInputRef = ref<HTMLElement | null>(null);
const countryPanelWidth = ref(0);

const updateCountryPanelWidth = () => {
    if (countryInputRef.value) {
        countryPanelWidth.value = countryInputRef.value.offsetWidth;
    }
};

const dialogRef = getDialogRef(ModalNames.ADD_ADDRESS);
const closeDialog = () => {
    dialogRef.value.close();
};

// Get modal data if provided
const modalData = computed(() => dialogRef.value?.data || {});

// Edit mode: an existing address record passed via EDIT_ADDRESS dialog data
const editRecord = computed<Address | undefined>(() => (modalData.value as { editAddress?: Address })?.editAddress);
const isEditMode = computed(() => !!editRecord.value);

// Function to find country by name (for prefilling from exposure)
const findCountryByName = (countryName: string) => {
    const normalized = countryName.toLowerCase().trim();
    return countriesData.find(c =>
        c.name.toLowerCase() === normalized ||
        c.code.toLowerCase() === normalized ||
        (normalized === 'us' || normalized === 'usa' || normalized === 'united states' || normalized === 'america') &&
        (c.code === 'US' || c.name === 'United States')
    );
};

const personStore = usePersonStore();
const vault_store = useVaultStore();
const isAddingAddress = ref(false);

// Keydown handlers to prevent invalid input
const handleAddressLineKeydown = (e: KeyboardEvent) => {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];

    // Allow copy/paste
    if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C' || e.key === 'v' || e.key === 'V' || e.key === 'x' || e.key === 'X' || e.key === 'a' || e.key === 'A')) {
        return;
    }

    // Allow dead keys for diacritic composition (macOS/Linux)
    if (e.key === 'Dead') return;

    const validPattern = /^[\p{L}0-9\s\-,.'/#]$/u;
    if (!validPattern.test(e.key) && !allowedKeys.includes(e.key)) {
        e.preventDefault();
    }
};

const handleCityStateKeydown = (e: KeyboardEvent) => {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];

    // Allow copy/paste
    if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C' || e.key === 'v' || e.key === 'V' || e.key === 'x' || e.key === 'X' || e.key === 'a' || e.key === 'A')) {
        return;
    }

    // Allow dead keys for diacritic composition (macOS/Linux)
    if (e.key === 'Dead') return;

    const validPattern = /^[\p{L}\s\-]$/u;
    if (!validPattern.test(e.key) && !allowedKeys.includes(e.key)) {
        e.preventDefault();
    }
};

const handlePostalKeydown = (e: KeyboardEvent) => {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    
    // Allow copy/paste
    if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C' || e.key === 'v' || e.key === 'V' || e.key === 'x' || e.key === 'X' || e.key === 'a' || e.key === 'A')) {
        return;
    }
    
    if (country.value === 'US') {
        // US: only numbers
        if (!/^\d$/.test(e.key) && !allowedKeys.includes(e.key)) {
            e.preventDefault();
        }
    } else {
        // Other countries: alphanumeric and hyphens
        const validPattern = /^[a-zA-Z0-9\-]$/;
        if (!validPattern.test(e.key) && !allowedKeys.includes(e.key)) {
            e.preventDefault();
        }
    }
};

// Input handlers for validation
const handleAddressLineInput = (e: Event, field: 'line1' | 'line2') => {
    const target = e.target as HTMLInputElement;
    const value = target.value;

    
    const cleaned = value.replace(/[^\p{L}0-9\s\-,.'/#]/gu, '');
    if (cleaned !== value) {
        if (field === 'line1') {
            line1.value = cleaned;
        } else {
            line2.value = cleaned;
        }
    }
};

const handleCityStateInput = (e: Event, field: 'city' | 'state') => {
    const target = e.target as HTMLInputElement;
    const value = target.value
    
    const cleaned = value.replace(/[^\p{L}\s\-]/gu, '');
    if (cleaned !== value) {
        if (field === 'city') {
            city.value = cleaned;
        } else {
            state.value = cleaned;
        }
    }
};

const onEnter = (e: KeyboardEvent) => {
    const hasValidCountry = country.value;
    const hasValidState = state.value && state.value.trim() !== '';

    if (hasValidCountry && hasValidState && line1.value !== '' && city.value !== '' && !isAddingAddress.value) {
        submitAddress();
    }
};

const onCountryChange = async  (countryCode: string) => {
    if (countryCode === 'RU') {
        showError(
            'Country Not Supported',
            'We’re sorry, but we cannot accept addresses from Russia due to international regulations.'
        )
        await nextTick();
        country.value = ''
        state.value = ''
        postal_code.value = ''
        return
    }
    postal_code.value = '';
    fetchStates(countryCode);
    // Reset state when country changes
    state.value = '';
};

const fetchStates = async (countryCode: string) => {
    const data = await personStore.fetchStatesByCode(countryCode);

    if(data && data.areas && Array.isArray(data.areas)) {
        // Map the API response to dropdown format
        stateOptions.value = data.areas.map((area: any) => ({
            label: area.name,  // "Alabama", "Alaska", etc.
            value: area.code   // "AL", "AK", etc.
        }));
    } else {
        stateOptions.value = [];
    }
};

const disableScroll = (event: WheelEvent) => {
  if (country.value === 'US') {
    (event.target as HTMLElement).blur();
    event.preventDefault();
  }
};

const onPostalInput = (e: Event) => {
    const target = e.target as HTMLInputElement;
    let value = target.value;
    
    if (country.value === 'US') {
        // US: only numbers, max 5 digits
        const cleaned = value.replace(/\D/g, '').slice(0, 5);
        postal_code.value = cleaned;
    } else {
        // Other countries: alphanumeric and hyphens, max 20 chars
        const cleaned = value.replace(/[^a-zA-Z0-9\-]/g, '').slice(0, 20);
        postal_code.value = cleaned;
    }
};

// Watch for modal data changes and prefill
watch(() => modalData.value?.address, async (addressData) => {
    if (addressData && typeof addressData === 'object') {
        // This is prefilled from exposure data - use simple text inputs
        isPrefilledFromExposure.value = true;

        try {
            // Handle different possible property names and populate text fields
            line1.value = addressData.line1 || addressData.line_1 || '';
            line2.value = addressData.line2 || addressData.line_2 || '';
            city.value = addressData.city || '';
            postal_code.value = addressData.postal_code || addressData.zip || addressData.zipcode || '';

            // Map country name/text to country code for dropdown
            let countryInput = addressData.country || addressData.country_code || '';

            // Default to United States if country is null or empty
            if (!countryInput || countryInput.trim() === '') {
                countryInput = 'United States';
            }

            // Find the country in our options and set the country code
            const foundCountry = findCountryByName(countryInput);
            country.value = foundCountry ? foundCountry.code : 'US';

            // Fetch states for the selected country and then set the state
            const stateInput = addressData.state || addressData.region || addressData.province || '';

            // If we have a country, fetch its states and then set the state value
            if (country.value) {
                await fetchStates(country.value);
                
                // Wait for nextTick to ensure stateOptions is updated
                await nextTick();

                // If we have state options, try to find matching state
                if (stateOptions.value.length > 0 && stateInput) {
                    const foundState = stateOptions.value.find(s =>
                        s.label.toLowerCase() === stateInput.toLowerCase() ||
                        s.value.toLowerCase() === stateInput.toLowerCase()
                    );
                    // Use label since PrimeSelect optionValue="label"
                    state.value = foundState ? foundState.label : stateInput;
                } else {
                    // No state options available, use text input
                    state.value = stateInput;
                }
            }
        } catch (error) {
            console.warn('Error prefilling address data:', error);
        }
    } else {
        // No prefilled data - use normal dropdown mode
        isPrefilledFromExposure.value = false;
    }
}, { immediate: true });

// Prefill the form when editing an existing address (EDIT_ADDRESS)
watch(editRecord, async (addr) => {
    if (!addr) return;

    line1.value = addr.line_1 || '';
    line2.value = addr.line_2 || '';
    city.value = addr.city || '';
    postal_code.value = addr.postal_code || '';

    const countryCode = addr.country || 'US';
    country.value = countryCode;

    // Load the matching state options first, then resolve the stored region.
    // The state PrimeSelect uses optionValue="label", so we must bind the option
    // LABEL — map a stored code (e.g. "GA") or label (e.g. "Georgia") to it.
    await fetchStates(countryCode);
    await nextTick();

    const regionInput = addr.region || '';
    if (stateOptions.value.length > 0 && regionInput) {
        const foundState = stateOptions.value.find(
            (s) =>
                s.label.toLowerCase() === regionInput.toLowerCase() ||
                s.value.toLowerCase() === regionInput.toLowerCase()
        );
        state.value = foundState ? foundState.label : regionInput;
    } else {
        // No dropdown options (free-text region country) — use the raw value
        state.value = regionInput;
    }
}, { immediate: true });

const addAddress = async () => {
    isAddingAddress.value = true;

    try {
        // Add the address to the person's vault
        await personStore.addAddress({
            line_1: line1.value,
            line_2: line2.value,
            city: city.value,
            region: state.value,
            postal_code: postal_code.value,
            country: country.value,
            primary: false,
        });

        // If this came from an exposure, suppress the original exposure
        if (modalData.value?.fromExposure && modalData.value?.exposureData) {
            const exposureData = modalData.value.exposureData;
            const exposureType = exposureData.type || 'address';
            await vault_store.updateExposure(true, exposureType, exposureData.value);
            await vault_store.updatePotentialExposures(exposureType, exposureData.value, true);

        }
        // Refresh the potential exposures to update the UI
        await vault_store.fetchPotentialExposures(true);
        isAddingAddress.value = false;
        closeDialog();
    } catch (error) {
        console.error('Error adding address:', error);
        isAddingAddress.value = false;
    }
};

const saveAddressEdit = async () => {
    const addr = editRecord.value;
    if (!addr?.id) return;

    isAddingAddress.value = true;

    try {
        const ok = await personStore.updateAddress(addr.id, {
            line_1: line1.value,
            line_2: line2.value,
            city: city.value,
            region: state.value,
            postal_code: postal_code.value,
            country: country.value,
        });

        // Keep the editor open if the save failed (error toast already shown)
        if (!ok) {
            isAddingAddress.value = false;
            return;
        }

        // Refresh potential exposures so matched records update in the UI
        await vault_store.fetchPotentialExposures(true);
        isAddingAddress.value = false;
        closeDialog();
    } catch {
        isAddingAddress.value = false;
    }
};

const submitAddress = () => {
    if (isEditMode.value) {
        saveAddressEdit();
    } else {
        addAddress();
    }
};
</script>
<style lang="scss" scoped>
.footer-actions {
    .p-btn-root_primary {
        @apply sm:px-3 px-2 py-2;
    }
}
.input-div {
    @apply flex flex-col items-start gap-2 flex-1 w-full;

    span {
        @apply font-inter text-zinc-500 text-sm font-normal leading-normal;
    }
}
.city-state-div {
    @apply flex sm:flex-row flex-col items-start gap-4 self-stretch;
}

</style>
